<template>
  <div ref="metricWrapper" class="w-metric" :class="context.rtl && 'items-end'">
    <div
      ref="metricTitle"
      class="items-center text-xs font-bold uppercase tracking-wide opacity-50 py-2 metric-title"
    >
      {{ titleToDisplay }}
    </div>
    <div ref="metricValue" class="items-center text-4xl font-bold metric-value">
      <slot />
    </div>
    <a
      target="_blank"
      class="list-view-link"
      rel="noopener noreferrer"
      ref="metricLink"
      :href="listViewUrl"
      v-if="embedded"
    >
      <span class="list-view-link-text">{{ this.$t('label.listviewlink') }}</span>
    </a>
  </div>
</template>
<script>
import { mapState } from 'vuex'
import FontSizeAdjustMixin from '@/mixins/FontSizeAdjustMixin'
import ListViewLinkMixin from '@/mixins/ListViewLinkMixin'

export default {
  mixins: [FontSizeAdjustMixin, ListViewLinkMixin],
  inheritAttrs: false,
  props: {
    titleText: String,
    defaultTitleText: String,
    embedded: Boolean,
  },
  inject: {
    context: { default: {} },
  },
  data() {
    return {
      titleTextProps: {
        maxFontSize: 20,
        elementName: 'metricTitle',
      },
      valueTextProps: {
        maxFontSize: 38,
        elementName: 'metricValue',
      },
      metricLinkProps: {
        elementName: 'metricLink',
        maxFontSize: 12,
      },
    }
  },
  computed: {
    titleToDisplay() {
      let title
      if (this.embedded) {
        title = this.titleText || this.defaultTitleText
      } else {
        title = this.storedTitleText || this.defaultTitleText
      }
      if (!title) {
        return ''
      }
      return title.substring(0, this.maxTitleLength)
    },
    ...mapState({
      maxTitleLength: (state) => state.ChartCategory.maxTitleLength,
      storedTitleText: (state) => state.ChartCategory.titleText,
    }),
  },
  watch: {
    storedTitleText() {
      this.$nextTick(this.scaleContent)
    },
  },
  methods: {
    scaleContent() {
      this.scaleText(this.titleTextProps)
      this.scaleText(this.valueTextProps)
      if (this.embedded) {
        this.scaleText(this.metricLinkProps)
      }
      this.scaleHeight(this.titleTextProps, this.valueTextProps, this.metricLinkProps)
    },
    scaleHeight(titleTextProps, valueTextProps, metricLinkProps) {
      let metricLinkHeight = 0
      if (this.embedded) {
        metricLinkHeight = this.$refs[metricLinkProps.elementName]
      }

      const titleRef = this.$refs[titleTextProps.elementName]
      const titleHeight = titleRef.scrollHeight
      const valueRef = this.$refs[valueTextProps.elementName]
      const valueHeight = valueRef.scrollHeight
      const wrapperHeight = this.$refs.metricWrapper.scrollHeight
      if (titleHeight + valueHeight > wrapperHeight - metricLinkHeight) {
        const newTextScale = (wrapperHeight - metricLinkHeight) / (titleHeight + valueHeight)
        const newTitleFontSize = parseInt(titleRef.style.fontSize, 10) * newTextScale
        this.updateTextSize(titleTextProps.elementName, newTitleFontSize)
        const newValueFontSize = parseInt(valueRef.style.fontSize, 10) * newTextScale
        this.updateTextSize(valueTextProps.elementName, newValueFontSize)
        if (this.embedded) {
          const newLinkFontSize =
            parseInt(this.$refs[metricLinkProps.elementName].style.fontSize, 10) * newTextScale
          this.updateTextSize(metricLinkProps.elementName, newLinkFontSize)
        }
      }
    },
  },
}
</script>
<style>
.w-metric {
  @apply flex flex-col justify-center w-full h-full items-center;
}
.metric-title,
.metric-value {
  @apply whitespace-no-wrap;
}
.list-view-link {
  @apply self-end pt-1 invisible;
}
.w-metric:hover .list-view-link {
  @apply visible;
}
.list-view-link-text {
  @apply text-gray-700 text-xs;
}
</style>
